<template>
	<div>
		<!-- Data Lists Section -->
		<div class="campaigns-section mb-4">
			<div class="section-header">
				<div class="section-header-content">
					<div class="d-flex align-items-center">
						<router-link to="/auto-campaigns" class="back-btn me-3" title="Back to Auto Campaigns">
							<i class="fad fa-arrow-left"></i>
						</router-link>
						<div>
							<h2 class="section-title">Campaign Data Lists</h2>
							<p class="section-subtitle">Manage and organize your campaign municipality lists</p>
						</div>
					</div>
				</div>
			</div>

			<!-- Search Only -->
			<div class="secondary-tabs-container">
				<div>
					<button
						v-if="selectedLists.length > 0"
						class="create-sequences-btn"
						@click="createSequencesCampaign"
						:title="`Create sequences campaign with ${selectedLists.length} selected list${selectedLists.length !== 1 ? 's' : ''}`"
					>
						<i class="fad fa-plus me-2"></i>
						<span class="btn-text">Create Campaign from Selected Lists</span>
						<span class="btn-badge">{{ selectedLists.length }}</span>
					</button>
				</div>
				<div class="search-input-wrapper">
					<i class="fad fa-search search-icon"></i>
					<input type="text" class="search-input" placeholder="Search data lists" v-model="searchTerm" @input="filterDataLists" />
				</div>
			</div>

			<!-- Data Lists Table -->
			<div class="campaigns-table">
				<!-- Table Header -->
				<div class="table-header">
					<div class="header-cell checkbox-col">
						<input
							type="checkbox"
							class="select-all-checkbox"
							:checked="allSelected"
							@change="toggleSelectAll"
							title="Select all data lists"
							:disabled="loadingDataLists"
						/>
					</div>
					<div class="header-cell campaign-col">Name</div>
					<div class="header-cell description-col">Description</div>
					<div class="header-cell created-by-col">Created By</div>
					<div class="header-cell date-col">Created Date</div>
					<div class="header-cell actions-col"></div>
				</div>

				<!-- Loading State -->
				<div v-if="loadingDataLists" class="table-loading">
					<div v-for="n in 5" :key="n" class="table-row skeleton-row">
						<div class="table-cell checkbox-cell">
							<div class="skeleton skeleton-checkbox"></div>
						</div>
						<div class="table-cell campaign-cell">
							<div class="campaign-info">
								<div class="skeleton skeleton-avatar"></div>
								<div class="campaign-details">
									<div class="skeleton skeleton-text skeleton-name"></div>
									<div class="skeleton skeleton-text skeleton-description"></div>
								</div>
							</div>
						</div>
						<div class="table-cell description-cell">
							<div class="skeleton skeleton-text skeleton-long"></div>
						</div>
						<div class="table-cell created-by-cell">
							<div class="created-by-info">
								<div class="skeleton skeleton-avatar-small"></div>
								<div class="skeleton skeleton-text skeleton-name"></div>
							</div>
						</div>
						<div class="table-cell date-cell">
							<div class="skeleton skeleton-text skeleton-date"></div>
						</div>
						<div class="table-cell actions-cell">
							<div class="skeleton skeleton-actions"></div>
						</div>
					</div>
				</div>

				<!-- Empty State -->
				<div v-else-if="filteredDataLists.length === 0 && !loadingDataLists" class="empty-state">
					<div class="empty-state-content">
						<div class="empty-state-icon">
							<i class="fad fa-database"></i>
						</div>
						<h3 class="empty-state-title">No data lists found</h3>
						<p class="empty-state-description">
							<span v-if="searchTerm">No data lists match your search criteria.</span>
							<span v-else>Create your first data list to organize campaign municipalities.</span>
						</p>
					</div>
				</div>

				<!-- Data Lists Rows -->
				<div v-else>
					<div v-for="dataList in filteredDataLists" :key="dataList.id" class="table-row">
						<!-- Checkbox -->
						<div class="table-cell checkbox-cell">
							<input
								type="checkbox"
								class="row-checkbox"
								:checked="selectedLists.includes(dataList.id)"
								@change="toggleSelectList(dataList.id)"
								:title="`Select ${dataList.name}`"
							/>
						</div>

						<!-- Name -->
						<div class="table-cell campaign-cell">
							<div class="campaign-info">
								<div class="campaign-avatar">
									<i class="fad fa-list"></i>
								</div>
								<div class="campaign-details">
									<div class="campaign-name">{{ dataList.name }}</div>
									<div class="campaign-description">{{ dataList.items_count }} items</div>
								</div>
							</div>
						</div>

						<!-- Description -->
						<div class="table-cell description-cell">
							<span class="description-text">{{ dataList.description }}</span>
						</div>

						<!-- Created By -->
						<div class="table-cell created-by-cell">
							<div class="created-by-info">
								<div class="user-avatar">
									{{ getUserInitials(dataList.author && dataList.author.name ? dataList.author.name : 'Unknown') }}
								</div>
								<span class="user-name">{{ dataList.author && dataList.author.name ? dataList.author.name : 'Unknown' }}</span>
							</div>
						</div>

						<!-- Created Date -->
						<div class="table-cell date-cell">{{ formatDate(dataList.created_at) }}</div>

						<!-- Actions -->
						<div class="table-cell actions-cell">
							<button type="button" class="action-icon-btn" @click="editDataList(dataList)" title="Edit">
								<i class="fad fa-pen"></i>
							</button>
							<button type="button" class="action-icon-btn" @click="viewDataListItems(dataList)" title="View all data in list">
								<i class="fad fa-list"></i>
							</button>
							<button type="button" class="action-icon-btn delete-btn" @click="deleteDataList(dataList)" title="Delete">
								<i class="fad fa-trash"></i>
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Edit Data List Offcanvas -->
		<div class="offcanvas offcanvas-end" tabindex="-1" id="editDataListOffcanvas" aria-labelledby="editDataListOffcanvasLabel">
			<div class="offcanvas-header">
				<h5 class="offcanvas-title" id="editDataListOffcanvasLabel">Edit Data List</h5>
				<button type="button" class="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
			</div>
			<div class="offcanvas-body">
				<form @submit.prevent="saveDataList">
					<div class="mb-3">
						<label for="editName" class="form-label">Name</label>
						<input type="text" class="form-control" id="editName" v-model="editingDataList.name" required placeholder="Enter data list name" />
					</div>
					<div class="mb-3">
						<label for="editDescription" class="form-label">Description</label>
						<textarea class="form-control" id="editDescription" v-model="editingDataList.description" rows="4" placeholder="Enter data list description"></textarea>
					</div>
					<div class="d-flex gap-2">
						<button type="submit" class="btn btn-primary" :disabled="isSaving">
							<i class="fad fa-save me-2"></i>
							{{ isSaving ? 'Saving...' : 'Save Changes' }}
						</button>
						<button type="button" class="btn btn-secondary" data-bs-dismiss="offcanvas">
							Cancel
						</button>
					</div>
				</form>
			</div>
		</div>

		<!-- Create Campaign Modal -->
		<div class="modal fade" id="createCampaignModal" tabindex="-1" aria-labelledby="createCampaignModalLabel" aria-hidden="true">
			<div class="modal-dialog modal-lg">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title" id="createCampaignModalLabel">Create Campaign from Selected Lists</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body">
						<!-- Campaign Details Form -->
						<div class="mb-4">
							<h6 class="fw-bold mb-3">Campaign Details</h6>
							<div class="mb-3">
								<label for="campaignName" class="form-label">Campaign Name <span class="text-danger">*</span></label>
								<input 
									type="text" 
									class="form-control" 
									id="campaignName" 
									v-model="campaignDetails.name" 
									placeholder="Enter campaign name"
									required
								/>
							</div>
							<div class="mb-3">
								<label for="campaignDescription" class="form-label">Description</label>
								<textarea 
									class="form-control" 
									id="campaignDescription" 
									v-model="campaignDetails.description" 
									rows="3"
									placeholder="Enter campaign description"
								></textarea>
							</div>
							<div class="mb-3">
								<label for="campaignUtm" class="form-label">UTM Campaign</label>
								<utm-campaign-input id="campaignUtm" v-model="campaignDetails.utm_campaign" />
							</div>
						</div>

						<!-- Selected Data Lists -->
						<div class="mb-4">
							<h6 class="fw-bold mb-3">Selected Data Lists ({{ selectedLists.length }})</h6>
							<div class="selected-lists-container">
								<div v-for="dataList in selectedDataLists" :key="dataList.id" class="selected-list-item">
									<div class="list-info">
										<div class="list-avatar">
											<i class="fad fa-list"></i>
										</div>
										<div class="list-details">
											<div class="list-name">{{ dataList.name }}</div>
											<div class="list-description">{{ dataList.items_count || 0 }} items</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						<!-- Campaign Type Selection -->
						<div class="mb-4">
							<h6 class="fw-bold mb-3">Campaign Type</h6>
							<div class="campaign-types">
								<div class="campaign-type-option selected" v-if="selectedCampaignType === 'sequence'">
									<input
										class="form-check-input"
										type="radio"
										name="campaignType"
										id="campaignTypeSequence"
										value="sequence"
										v-model="selectedCampaignType"
										checked
									/>
									<label class="campaign-type-label" for="campaignTypeSequence">
										<div class="type-header">
											<i class="fad fa-stream me-2"></i>
											<span class="type-name">Sequence Campaign</span>
											<span class="badge bg-success ms-2">Available</span>
										</div>
										<div class="type-description">Multi-step automated email sequences with timing controls</div>
									</label>
								</div>

								<div class="campaign-type-option" v-else>
									<input class="form-check-input" type="radio" name="campaignType" id="campaignTypeSequence" value="sequence" v-model="selectedCampaignType" />
									<label class="campaign-type-label" for="campaignTypeSequence">
										<div class="type-header">
											<i class="fad fa-stream me-2"></i>
											<span class="type-name">Sequence Campaign</span>
											<span class="badge bg-success ms-2">Available</span>
										</div>
										<div class="type-description">Multi-step automated email sequences with timing controls</div>
									</label>
								</div>

								<div class="campaign-type-option">
									<input class="form-check-input" type="radio" name="campaignType" id="campaignTypeRegular" value="regular" v-model="selectedCampaignType" />
									<label class="campaign-type-label" for="campaignTypeRegular">
										<div class="type-header">
											<i class="fad fa-envelope me-2"></i>
											<span class="type-name">Campaign</span>
											<span class="badge bg-warning ms-2">In Development</span>
										</div>
										<div class="type-description">Traditional email campaign with single send</div>
									</label>
								</div>

								<div class="campaign-type-option">
									<input class="form-check-input" type="radio" name="campaignType" id="campaignTypeAuto" value="auto" v-model="selectedCampaignType" />
									<label class="campaign-type-label" for="campaignTypeAuto">
										<div class="type-header">
											<i class="fad fa-robot me-2"></i>
											<span class="type-name">Auto Campaign</span>
											<span class="badge bg-warning ms-2">In Development</span>
										</div>
										<div class="type-description">AI-powered automated campaigns with smart targeting</div>
									</label>
								</div>
							</div>
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-secondary" data-bs-dismiss="modal" :disabled="isCreatingCampaign">Cancel</button>
						<button type="button" class="btn btn-primary" @click="createCampaignFromLists" :disabled="selectedLists.length === 0 || !campaignDetails.name.trim() || isCreatingCampaign">
							<i class="fad me-2" :class="[isCreatingCampaign ? 'fa-spinner fa-spin' : 'fa-plus']"></i>
							{{ isCreatingCampaign ? 'Creating...' : 'Create Campaign' }}
						</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import restAPI from '@/api/index.js'
import { Offcanvas, Modal } from 'bootstrap'
import UtmCampaignInput from '@/components/common/UtmCampaignInput'
import { normalizeUtmCampaign } from '@/utils/utm'

export default {
	name: 'AutoCampaignsDataLists',
	components: {
		UtmCampaignInput,
	},
	data() {
		return {
			searchTerm: '',
			selectedLists: [],
			dataLists: [],
			filteredDataLists: [],
			loadingDataLists: false,
			editingDataList: {
				id: null,
				name: '',
				description: '',
			},
			isSaving: false,
			isDeleting: false,
			selectedCampaignType: 'sequence',
			campaignDetails: {
				name: '',
				description: '',
				utm_campaign: '',
			},
			isCreatingCampaign: false,
			$editOffcanvas: null,
			$createCampaignModal: null,
		}
	},
	computed: {
		allSelected() {
			return this.filteredDataLists.length > 0 && this.selectedLists.length === this.filteredDataLists.length
		},
		selectedDataLists() {
			return this.dataLists.filter(list => this.selectedLists.includes(list.id))
		},
	},
	async mounted() {
		await this.loadCampaignDataLists()
		this.filteredDataLists = [...this.dataLists]
		this.$editOffcanvas = new Offcanvas('#editDataListOffcanvas')
		this.$createCampaignModal = new Modal('#createCampaignModal')
	},
	methods: {
		async loadCampaignDataLists() {
			try {
				this.loadingDataLists = true
				const response = await restAPI.get(`campaign-data-lists`)
				this.dataLists = response.data
			} catch (error) {
				console.error('Error loading campaign data lists:', error)
				this.dataLists = []
			} finally {
				this.loadingDataLists = false
			}
		},
		filterDataLists() {
			if (!this.searchTerm.trim()) {
				this.filteredDataLists = [...this.dataLists]
			} else {
				const searchLower = this.searchTerm.toLowerCase()
				this.filteredDataLists = this.dataLists.filter(
					dataList =>
						dataList.name.toLowerCase().includes(searchLower) ||
						dataList.description.toLowerCase().includes(searchLower) ||
						(dataList.author && dataList.author.name ? dataList.author.name : '').toLowerCase().includes(searchLower)
				)
			}

			// Clear selections for items not in filtered results
			this.selectedLists = this.selectedLists.filter(id => this.filteredDataLists.some(list => list.id === id))
		},
		toggleSelectAll() {
			if (this.allSelected) {
				// Deselect all visible items
				this.selectedLists = this.selectedLists.filter(id => !this.filteredDataLists.some(list => list.id === id))
			} else {
				// Select all visible items
				const visibleIds = this.filteredDataLists.map(list => list.id)
				this.selectedLists = [...new Set([...this.selectedLists, ...visibleIds])]
			}
		},
		toggleSelectList(listId) {
			const index = this.selectedLists.indexOf(listId)
			if (index === -1) {
				this.selectedLists.push(listId)
			} else {
				this.selectedLists.splice(index, 1)
			}
		},
		getUserInitials(userName) {
			return userName
				.split(' ')
				.map(name => name.charAt(0))
				.join('')
				.toUpperCase()
		},
		getInitials(name) {
			return name
				.split(' ')
				.map(word => word.charAt(0))
				.join('')
				.toUpperCase()
		},
		formatDate(dateString) {
			const date = new Date(dateString)
			return date.toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
			})
		},
		formatRelativeTime(dateString) {
			const date = new Date(dateString)
			const now = new Date()
			const diffTime = Math.abs(now - date)

			const diffSeconds = Math.floor(diffTime / 1000)
			const diffMinutes = Math.floor(diffSeconds / 60)
			const diffHours = Math.floor(diffMinutes / 60)
			const diffDays = Math.floor(diffHours / 24)

			if (diffSeconds < 60) return 'Just now'
			if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`
			if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`
			if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`
			if (diffDays < 30) {
				const weeks = Math.floor(diffDays / 7)
				return `${weeks} week${weeks !== 1 ? 's' : ''} ago`
			}
			if (diffDays < 365) {
				const months = Math.floor(diffDays / 30)
				return `${months} month${months !== 1 ? 's' : ''} ago`
			}
			const years = Math.floor(diffDays / 365)
			return `${years} year${years !== 1 ? 's' : ''} ago`
		},
		viewDataList(dataList) {
			console.log('View data list:', dataList.name)
			// TODO: Navigate to data list view
		},
		editDataList(dataList) {
			this.editingDataList = {
				id: dataList.id,
				name: dataList.name,
				description: dataList.description,
			}

			// Open the offcanvas
			this.$editOffcanvas.show()
		},
		createSequencesCampaign() {
			// Clear the form when opening the modal
			this.campaignDetails.name = ''
			this.campaignDetails.description = ''
			this.campaignDetails.utm_campaign = ''
			this.selectedCampaignType = 'sequence'
			
			// Open the campaign creation modal
			this.$createCampaignModal.show()
		},
		async createCampaignFromLists() {
			// Validate required fields
			if (!this.campaignDetails.name.trim()) {
				this.$swal.fire({
					icon: 'error',
					title: 'Validation Error',
					text: 'Campaign name is required.',
					timer: 3000,
					showConfirmButton: false,
				})
				return
			}

			// Handle different campaign types
			if (this.selectedCampaignType !== 'sequence') {
				this.$swal.fire({
					icon: 'warning',
					title: 'Feature In Development',
					text: `${this.selectedCampaignType === 'regular' ? 'Campaign' : 'Auto Campaign'} feature is currently in development.`,
					timer: 3000,
					showConfirmButton: false,
				})
				return
			}

			// Create sequence campaign
			try {
				this.isCreatingCampaign = true

				const campaignData = {
					name: this.campaignDetails.name.trim(),
					description: this.campaignDetails.description.trim(),
					utm_campaign: normalizeUtmCampaign(this.campaignDetails.utm_campaign),
					data_list_ids: this.selectedLists,
				}

				const response = await restAPI.post('/sequenced-campaigns', campaignData)

				// Close the modal
				this.$createCampaignModal.hide()

				// Show success message
				this.$swal.fire({
					icon: 'success',
					title: 'Campaign Created Successfully!',
					text: `${this.campaignDetails.name} has been created and is ready for editing.`,
					timer: 2000,
					showConfirmButton: false,
				})

				// Navigate to the AutoCampaignWithSequencesEdit view
				this.$router.push(`/auto-campaigns/edit/${response.data.id}/sequences`)

			} catch (error) {
				console.error('Error creating sequence campaign:', error)
				let errorMessage = 'An unexpected error occurred while creating the campaign.'
				if (error.response && error.response.data && error.response.data.message) {
					errorMessage = error.response.data.message
				}
				this.$swal.fire({
					icon: 'error',
					title: 'Failed to Create Campaign',
					text: errorMessage,
				})
			} finally {
				this.isCreatingCampaign = false
			}
		},
		async deleteDataList(dataList) {
			const result = await this.$swal.fire({
				title: 'Are you sure?',
				text: 'This action cannot be undone!',
				icon: 'warning',
				showCancelButton: true,
				confirmButtonColor: '#d33',
				cancelButtonColor: '#3085d6',
				confirmButtonText: 'Yes, delete it!',
				// Override global toast settings for this dialog
				toast: false,
				position: 'center',
				showConfirmButton: true,
				timer: null,
				timerProgressBar: false,
			})
			if (result.isConfirmed) {
				try {
					this.isDeleting = true
					await restAPI.delete(`/campaign-data-lists/${dataList.id}`)
					this.$swal.fire({
						icon: 'success',
						title: 'Data list deleted successfully!',
						timer: 3000,
						showConfirmButton: false,
					})
					// Refresh the data lists
					await this.loadCampaignDataLists()
					this.filteredDataLists = [...this.dataLists]
				} catch (error) {
					console.error('Error deleting data list:', error)
					let errorMessage = 'An unexpected error occurred.'
					if (error.response && error.response.data && error.response.data.message) {
						errorMessage = error.response.data.message
					}
					this.$swal.fire({
						icon: 'error',
						title: 'Failed to delete data list',
						text: errorMessage,
					})
				} finally {
					this.isDeleting = false
				}
			}
		},
		async saveDataList() {
			try {
				this.isSaving = true

				const response = await restAPI.put(`/campaign-data-lists/${this.editingDataList.id}`, {
					name: this.editingDataList.name,
					description: this.editingDataList.description,
				})

				// Update the local data list
				const index = this.dataLists.findIndex(list => list.id === this.editingDataList.id)
				if (index !== -1) {
					this.dataLists[index] = { ...this.dataLists[index], ...response.data }
					this.filterDataLists() // Refresh filtered list
				}

				// Close the offcanvas
				this.$editOffcanvas.hide()

				// Show success message
				this.$swal.fire({
					icon: 'success',
					title: 'Data List Updated',
					text: `${this.editingDataList.name} has been updated successfully.`,
					timer: 3000,
					timerProgressBar: true,
				})
			} catch (error) {
				console.error('Error updating data list:', error)
				this.$swal.fire({
					icon: 'error',
					title: 'Failed to Update',
					text: 'An error occurred while updating the data list.',
					confirmButtonText: 'Close',
				})
			} finally {
				this.isSaving = false
			}
		},
		viewDataListItems(dataList) {
			// Navigate to the data list items page
			this.$router.push({
				name: 'DataListItems',
				params: {
					id: dataList.id,
				},
			})
		},
	},
}
</script>

<style scoped>
/* Campaigns Section */
.campaigns-section {
	background: white;
	border-radius: 8px;
	overflow: hidden;
}

.section-header {
	padding: 24px 24px 16px 24px;
}

.section-header-content {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	margin-bottom: 8px;
}

.back-btn {
	width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #ffffff;
	border: 1px solid #e0e1e1;
	border-radius: 50%;
	color: #64748b;
	text-decoration: none;
	transition: all 0.2s ease;
	flex-shrink: 0;
}

.back-btn:hover {
	background: #f8f9fa;
	border-color: #d1d5db;
	color: #475569;
	text-decoration: none;
}

.section-title {
	color: #111827;
	margin: 0 0 4px 0;
}

.section-subtitle {
	font-size: 14px;
	color: #6b7280;
	margin: 0;
}

/* Secondary Tabs Container */
.secondary-tabs-container {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 0 24px 24px 24px;
}

/* Create Sequences Button */
.create-sequences-btn {
	display: inline-flex;
	align-items: center;
	padding: 8px 16px;
	background: none;
	border: none;
	font-size: 14px;
	font-weight: 500;
	color: #6b7280;
	cursor: pointer;
	border-radius: 20px;
	transition: all 0.2s ease;
	white-space: nowrap;
	gap: 8px;
	background-color: #f3f4f6;
}

.create-sequences-btn:hover {
	color: #374151;
	background-color: #e5e7eb;
}

.create-sequences-btn i {
	font-size: 14px;
}

.btn-text {
	font-weight: 500;
}

.btn-badge {
	background-color: #e5e7eb;
	color: #6b7280;
	padding: 2px 8px;
	border-radius: 12px;
	font-size: 12px;
	font-weight: 600;
	min-width: 20px;
	text-align: center;
}

.create-sequences-btn:hover .btn-badge {
	background-color: #dbeafe;
	color: #1e40af;
}

/* Search Input */
.search-input-wrapper {
	position: relative;
	width: 300px;
}

.search-icon {
	position: absolute;
	left: 12px;
	top: 50%;
	transform: translateY(-50%);
	color: #9ca3af;
	font-size: 14px;
}

.search-input {
	width: 100%;
	padding: 8px 12px 8px 36px;
	border: 1px solid #d1d5db;
	border-radius: 6px;
	font-size: 14px;
	outline: none;
	transition: border-color 0.2s ease;
	background: white;
}

.search-input:focus {
	border-color: #3b82f6;
	box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Campaigns Table */
.campaigns-table {
	border-top: 1px solid #f3f4f6;
}

.table-header {
	display: grid;
	grid-template-columns: 50px 2fr 2fr 1.5fr 1fr 120px;
	gap: 12px;
	padding: 12px 24px;
	background-color: #f9fafb;
	border-bottom: 1px solid #f3f4f6;
}

.header-cell {
	font-size: 12px;
	font-weight: 600;
	color: #6b7280;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

.table-row {
	display: grid;
	grid-template-columns: 50px 2fr 2fr 1.5fr 1fr 120px;
	gap: 12px;
	align-items: center;
	padding: 16px 24px;
	border-bottom: 1px solid #f9fafb;
	transition: background-color 0.2s ease;
}

.table-row:hover {
	background-color: #f9fafb;
}

.table-row:last-child {
	border-bottom: none;
}

.table-cell {
	font-size: 14px;
	color: #111827;
}

/* Campaign Info */
.campaign-info {
	display: flex;
	align-items: center;
	gap: 12px;
}

.campaign-avatar {
	width: 40px;
	height: 40px;
	border-radius: 50%;
	background-color: #e5e7eb;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 14px;
	font-weight: 600;
	color: #374151;
	flex-shrink: 0;
}

.campaign-details {
	min-width: 0;
}

.campaign-name {
	font-size: 14px;
	font-weight: 500;
	color: #111827;
	line-height: 1.4;
	display: flex;
	align-items: center;
	gap: 8px;
}

.campaign-description {
	font-size: 12px;
	color: #6b7280;
	line-height: 1.4;
}

/* Description Column */
.description-cell {
	color: #6b7280;
}

.description-text {
	font-size: 14px;
	color: #6b7280;
	line-height: 1.4;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
}

/* Created By Column */
.created-by-info {
	display: flex;
	align-items: center;
	gap: 8px;
}

.user-avatar {
	width: 32px;
	height: 32px;
	border-radius: 50%;
	background-color: #e5e7eb;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 12px;
	font-weight: 600;
	color: #374151;
	flex-shrink: 0;
}

.user-name {
	font-size: 14px;
	color: #111827;
	font-weight: 500;
}

/* Checkbox Column */
.checkbox-col,
.checkbox-cell {
	display: flex;
	align-items: center;
	justify-content: center;
}

.select-all-checkbox,
.row-checkbox {
	width: 16px;
	height: 16px;
	cursor: pointer;
	accent-color: #3b82f6;
}

.select-all-checkbox:hover,
.row-checkbox:hover {
	transform: scale(1.1);
}

/* Date Column */
.date-cell {
	color: #6b7280;
	font-size: 13px;
}

/* Actions */
.actions-cell {
	display: flex;
	gap: 4px;
	align-items: center;
	justify-content: flex-end;
}

.action-icon-btn {
	width: 32px;
	height: 32px;
	border: none;
	background: transparent;
	color: #9ca3af;
	cursor: pointer;
	border-radius: 4px;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 14px;
	transition: all 0.2s ease;
}

.action-icon-btn:hover {
	background-color: #f3f4f6;
	color: #6b7280;
}

.action-icon-btn.delete-btn:hover {
	background-color: #fee2e2;
	color: #dc2626;
}

/* Empty State */
.empty-state {
	padding: 60px 24px;
	text-align: center;
}

.empty-state-content {
	max-width: 400px;
	margin: 0 auto;
}

.empty-state-icon {
	width: 80px;
	height: 80px;
	border-radius: 50%;
	background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
	display: flex;
	align-items: center;
	justify-content: center;
	margin: 0 auto 24px;
	box-shadow: 0 8px 32px rgba(102, 126, 234, 0.2);
}

.empty-state-icon i {
	font-size: 32px;
	color: white;
}

.empty-state-title {
	font-size: 20px;
	font-weight: 600;
	color: #1f2937;
	margin-bottom: 12px;
	line-height: 1.3;
}

.empty-state-description {
	color: #64748b;
	font-size: 16px;
	line-height: 1.5;
	margin: 0;
}

/* Skeleton Loading Styles */
.table-loading {
	background: white;
}

.skeleton-row {
	background: white;
}

.skeleton-row:hover {
	background: white;
}

.skeleton {
	background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
	background-size: 200% 100%;
	animation: loading 1.5s infinite;
	border-radius: 4px;
}

@keyframes loading {
	0% {
		background-position: 200% 0;
	}
	100% {
		background-position: -200% 0;
	}
}

.skeleton-checkbox {
	width: 16px;
	height: 16px;
	border-radius: 2px;
}

.skeleton-avatar {
	width: 40px;
	height: 40px;
	border-radius: 50%;
}

.skeleton-avatar-small {
	width: 32px;
	height: 32px;
	border-radius: 50%;
}

.skeleton-text {
	height: 16px;
	border-radius: 4px;
}

.skeleton-name {
	width: 120px;
	margin-bottom: 4px;
}

.skeleton-description {
	width: 80px;
	height: 12px;
}

.skeleton-long {
	width: 200px;
}

.skeleton-date {
	width: 90px;
}

.skeleton-actions {
	width: 90px;
	height: 32px;
	border-radius: 4px;
}

/* Campaign Creation Modal Styles */
.selected-lists-container {
	max-height: 200px;
	overflow-y: auto;
	border: 1px solid #e5e7eb;
	border-radius: 6px;
	padding: 12px;
	background: #f9fafb;
}

.selected-list-item {
	display: flex;
	align-items: center;
	padding: 8px 0;
	border-bottom: 1px solid #e5e7eb;
}

.selected-list-item:last-child {
	border-bottom: none;
}

.list-info {
	display: flex;
	align-items: center;
	gap: 12px;
}

.list-avatar {
	width: 32px;
	height: 32px;
	border-radius: 50%;
	background-color: #e5e7eb;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 12px;
	font-weight: 600;
	color: #374151;
	flex-shrink: 0;
}

.list-details {
	min-width: 0;
}

.list-name {
	font-size: 14px;
	font-weight: 500;
	color: #111827;
	line-height: 1.4;
}

.list-description {
	font-size: 12px;
	color: #6b7280;
	line-height: 1.4;
}

.campaign-types {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.campaign-type-option {
	position: relative;
	border: 2px solid #e5e7eb;
	border-radius: 8px;
	padding: 16px;
	transition: all 0.2s ease;
	cursor: pointer;
}

.campaign-type-option:hover {
	border-color: #3b82f6;
	background: #f8fafc;
}

.campaign-type-option.selected {
	border-color: #3b82f6;
	background: #eff6ff;
}

.campaign-type-option input[type='radio'] {
	position: absolute;
	top: 16px;
	right: 16px;
	width: 16px;
	height: 16px;
	accent-color: #3b82f6;
}

.campaign-type-label {
	cursor: pointer;
	margin: 0;
	padding-right: 40px;
}

.type-header {
	display: flex;
	align-items: center;
	margin-bottom: 6px;
}

.type-name {
	font-size: 16px;
	font-weight: 600;
	color: #111827;
}

.type-description {
	font-size: 14px;
	color: #6b7280;
	line-height: 1.4;
}

</style>
